// src/components/Chat/ChatRoomView.jsx

import { useEffect, useState, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Divider,
    useMediaQuery,
    Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useUser } from '../../contexts/UserContext';
import api from '../../api/axios';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import { buildWsUrl } from '../../utils/websocket';

const ChatRoomView = ({ category, categoryId, nickname }) => {
    const { userInfo } = useUser(); // ← 여기 추가
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [socket, setSocket] = useState(null);

    const [messages, setMessages] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);

    const containerRef = useRef();
    const MESSAGE_LIMIT = 50;

    useEffect(() => {
        setMessages([]);
        setOffset(0);
        setHasMore(true);
    }, [categoryId]);

    const loadPreviousMessages = useCallback(async () => {
        if (!hasMore) return;
        try {
            const res = await api.get(`/api/chat/categories/${categoryId}/messages/`, {
                params: { limit: MESSAGE_LIMIT, offset },
            });
            const newMessages = (res.data.messages || []).reverse();
            if (newMessages.length < MESSAGE_LIMIT) setHasMore(false);
            setOffset(prev => prev + newMessages.length);
            setMessages(prev => [...newMessages, ...prev]);
        } catch (e) {
            console.error('이전 메시지 불러오기 실패:', e);
        }
    }, [offset, categoryId, hasMore]);


    useEffect(() => {
        const loadInitialMessages = async () => {
            try {
                const res = await api.get(`/api/chat/categories/${categoryId}/messages/`, {
                    params: { limit: MESSAGE_LIMIT, offset: 0 },
                });

                const initialMessages = (res.data.messages || []).reverse();
                setMessages(initialMessages);
                setOffset(initialMessages.length);
                if (initialMessages.length < MESSAGE_LIMIT) setHasMore(false);
                setLoading(false);

                if (initialMessages.length > 0) {
                    const lastId = initialMessages[initialMessages.length - 1]?.id;
                    if (lastId) {
                        await api.post(`/api/chat/categories/${categoryId}/read/`, {
                            last_read_id: lastId,
                        });
                    }
                }

                setTimeout(() => {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = containerRef.current.scrollHeight;
                    }
                }, 0);
            } catch (err) {
                console.error('초기 메시지 불러오기 실패:', err);
                setLoading(false);
            }
        };

        setLoading(true);
        loadInitialMessages();
    }, [categoryId]);

    const handleSend = (newMessage) => {
        setMessages((prev) => [...prev, newMessage]);

        setTimeout(() => {
            if (containerRef.current) {
                containerRef.current.scrollTop = containerRef.current.scrollHeight;
            }
        }, 0);
    };

    useEffect(() => {
        // util 함수로 WebSocket URL 생성
        const wsPath = `/ws/chat/category/${categoryId}/`;  // ← 경로만 넘겨줌
        const s = new WebSocket(buildWsUrl(wsPath));        // ✅ 한 줄로 끝!

        setSocket(s);

        s.onopen = () => console.log('✅ WebSocket 연결됨');

        s.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data);
                if (data.type === 'chat.message') {
                    // is_mine 플래그는 MessageList에서 sender_id로 다시 계산하므로 굳이 넣지 않아도 OK
                    setMessages((prev) => [...prev, data]);
                }
            } catch (err) {
                console.error('❌ JSON 파싱 실패:', err);
            }
        };

        s.onclose = () => console.log('❌ WebSocket 연결 종료');

        return () => s.close();
    }, [categoryId, userInfo.id,]);

    if (loading) return <CircularProgress sx={{ m: 4 }} />;

    return (
        <Box sx={{ p: isMobile ? 1.5 : 4 }}>
            <Typography
                variant="h4"
                gutterBottom
                sx={{
                    fontSize: isMobile ? '1.30rem' : undefined,
                    fontWeight: 600,
                }}
            >
                {category.emoji} {category.name}
            </Typography>

            {hasMore ? (
                <Box textAlign="center" sx={{ mt: 1, mb: 2 }}>
                    <Button onClick={loadPreviousMessages} size="small" variant="outlined">
                        이전 채팅 더보기
                    </Button>
                </Box>
            ) : (
                <Box textAlign="center" sx={{ mt: 1, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        더 이상 불러올 채팅이 없습니다.
                    </Typography>
                </Box>
            )}

            <Paper
                sx={{
                    p: 0,
                    overflow: 'visible',
                    boxShadow: 'none',
                    mb: 2,
                    width: isMobile ? '100%' : undefined,
                    mx: isMobile ? 'auto' : 0,
                }}
            >
                <MessageList messages={messages} containerRef={containerRef} nickname={nickname} />
            </Paper>

            <Divider sx={{ mb: 2 }} />

            {/* 항상 렌더하고 내부에서 닉네임 조건 처리 */}
            <MessageInput
                categoryId={categoryId}
                onSend={handleSend}
                nickname={nickname}
                socket={socket}
            />
        </Box>
    );
};

export default ChatRoomView;
